import { useCallback, useEffect, useRef, useState } from 'react';

import { shouldSettleCheckoutRepriceFromPricedOrder } from 'features/checkout/hooks/delivery-reprice-utils';
import {
  diagnosticMoney,
  projectDiagnosticError,
  projectPricingInput,
} from 'features/diagnostics/context/projectors';
import {
  useDiagnosticPricingRecorder,
  useDiagnosticToolsEnabled,
} from 'features/diagnostics/context/provider';
import { safelyObserveDiagnostics } from 'features/diagnostics/context/safe-observation';
import { getStoredOrderId } from 'features/order/utils/stored-order-id';
import { useStoreContext } from 'features/store';

import {
  DeliveryStatus,
  IPriceDeliveryInput,
  IPriceOrderInput,
  usePriceOrderMutation,
} from 'generated/graphql-gateway';
import usePosVendor from 'hooks/menu/use-pos-vendor';
import { useOrderStatus } from 'hooks/order-status';
import { useCart } from 'hooks/use-cart';
import useIsSignUpAfterCartEnabled from 'hooks/use-is-sign-up-after-cart';
import { actions, useAppDispatch } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { OrderStatus } from 'state/order/types';
import { useServiceModeLive } from 'state/service-mode';
import { useUIContext } from 'state/ui';
import { buildPriceDeliveryInput, buildPriceOrderInput } from 'utils/cart';
import { computeOtherDiscountAmount } from 'utils/cart/helper';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import { logger } from 'utils/logger';
import { TraceName, startTrace, stopTrace } from 'utils/performance/trace';
import { isDelivery } from 'utils/service-mode';

import { IPriceOrderParams, IUsePriceOrderParams } from './types';

// default order status polling interval, in ms
const DEFAULT_POLL_INTERVAL = 1000;
const PRICING_RETRY_LIMIT = 2; // Attempt reprice 3 times

const failureStatuses = {
  delivery: [DeliveryStatus.QUOTE_ERROR, DeliveryStatus.QUOTE_UNAVAILABLE],
  pos: [OrderStatus.PRICE_ERROR],
};

interface PricingCartRequestContext {
  sequence: number;
  cartIdentity: number;
  requestedAmountCents: number;
}

// note that serverOrder contains statuses for commit and price order, we have to make sure that
// the statuses we check here belongs to pricing errors statuses
export const priceOrderErrorStatuses = [...failureStatuses.delivery, ...failureStatuses.pos];

export const usePriceOrder = ({
  cartEntries,
  enabled = true,
  orderStatusPollInterval = DEFAULT_POLL_INTERVAL,
  updateTipAmount,
}: IUsePriceOrderParams) => {
  const cartIdentityRef = useRef({ cartEntries, identity: 0 });
  if (cartIdentityRef.current.cartEntries !== cartEntries) {
    cartIdentityRef.current = {
      cartEntries,
      identity: cartIdentityRef.current.identity + 1,
    };
  }
  const currentCartIdentity = cartIdentityRef.current.identity;
  const { calculateCartTotalWithDiscount } = useCart();
  const {
    checkoutRepriceRequestId,
    checkoutRepriceRequestCompletedId,
    deliveryAddress,
    quoteId,
    setCheckoutRepriceRequestCompletedId,
    setQuoteId,
  } = useOrderContext();
  const { store } = useStoreContext();
  const serviceMode = useServiceModeLive();
  const { formatCurrencyForLocale } = useUIContext();
  const formatCurrencyForLocaleRef = useRef(formatCurrencyForLocale);
  useEffect(() => {
    formatCurrencyForLocaleRef.current = formatCurrencyForLocale;
  }, [formatCurrencyForLocale]);
  const isSignUpAfterCart = useIsSignUpAfterCartEnabled();
  const [fireMutation, mutationResult] = usePriceOrderMutation({
    context: { shouldNotBatch: true },
  });
  const enablePaymentErrorContinue = useFlag(LaunchDarklyFlag.ENABLE_PAYMENT_ERROR_CONTINUE);
  const { vendor } = usePosVendor();
  const [didOrderPricingRetryFail, setDidOrderPricingRetryFail] = useState(false);
  const orderPricingAttemptsRef = useRef<number>(0);
  const diagnosticAttemptRef = useRef<number | null>(null);
  const diagnosticAttemptByOrderIdRef = useRef(new Map<string, number | null>());
  const diagnosticPricing = useDiagnosticPricingRecorder();
  const diagnosticPricingRef = useRef(diagnosticPricing);
  diagnosticPricingRef.current = diagnosticPricing;
  const diagnosticsEnabled = useDiagnosticToolsEnabled();
  const diagnosticsEnabledRef = useRef(diagnosticsEnabled);
  diagnosticsEnabledRef.current = diagnosticsEnabled;
  if (!diagnosticsEnabled) {
    diagnosticAttemptRef.current = null;
    diagnosticAttemptByOrderIdRef.current.clear();
  }
  const dispatch = useAppDispatch();

  // Guards against stale pricing responses overwriting the latest rbiOrderId.
  // When multiple priceOrder mutations are in-flight (e.g., initial pricing + reward-triggered
  // reprice), Apollo's useMutation updates data with whichever response arrives last
  // chronologically, not the last requested. This ref + state pair ensures only the most
  // recent mutation's response is accepted.
  const pricingSequenceRef = useRef(0);
  const [acceptedRbiOrderId, setAcceptedRbiOrderId] = useState<string | null>(null);
  const latestPricingCartRequestRef = useRef<PricingCartRequestContext | null>(null);
  const acceptedPricingCartRequestRef = useRef<
    (PricingCartRequestContext & { rbiOrderId: string }) | null
  >(null);
  // Capture the reprice request id for this pricing attempt so a late poll
  // success cannot advance completion past a newer address/store change.
  const pricingRepriceRequestIdRef = useRef(0);

  const successStatuses = enablePaymentErrorContinue
    ? [
        OrderStatus.PRICE_SUCCESSFUL,
        // This will prevent this price order query from being called again
        // if the order is already inserted
        OrderStatus.INSERT_SUCCESSFUL,
        OrderStatus.PAYMENT_ERROR,
        OrderStatus.VALIDATION_ERROR,
      ]
    : [OrderStatus.PRICE_SUCCESSFUL, OrderStatus.VALIDATION_ERROR];

  const { cartTotal = 0, isCartTotalNegative = false } =
    calculateCartTotalWithDiscount?.(cartEntries) || {};
  const diagnosticInputRef = useRef<{
    cartTotal?: number;
    serviceMode?: typeof serviceMode;
    storeNumber?: string;
  }>({});
  diagnosticInputRef.current = diagnosticsEnabled
    ? { cartTotal, serviceMode, storeNumber: store?.number ?? undefined }
    : {};

  const existingOrder = enabled ? getStoredOrderId() : null;
  const rbiOrderId = enabled ? acceptedRbiOrderId ?? existingOrder ?? null : null;

  const orderStatusQueryResult = useOrderStatus({
    failureStatuses,
    onSuccess(rbiOrder) {
      // Store pricedOrderId in Redux when pricing succeeds
      dispatch(actions.ordering.setPricedOrderId(rbiOrder.rbiOrderId));

      const requestIdForAttempt = pricingRepriceRequestIdRef.current;
      if (
        shouldSettleCheckoutRepriceFromPricedOrder({
          checkoutRepriceRequestId: requestIdForAttempt,
          checkoutRepriceRequestCompletedId,
          serviceMode,
          quoteId,
          deliveryAddress,
          serverOrder: rbiOrder,
          selectedStoreNumber: store?.number ?? null,
        })
      ) {
        if (rbiOrder.delivery?.quoteId) {
          setQuoteId(rbiOrder.delivery.quoteId);
        }
        setCheckoutRepriceRequestCompletedId(current => Math.max(current, requestIdForAttempt));
      }

      if (isDelivery(rbiOrder.cart.serviceMode)) {
        const otherDiscountAmount = computeOtherDiscountAmount(rbiOrder.cart.discounts);
        if (updateTipAmount) {
          updateTipAmount({
            subTotalCents: rbiOrder.cart.subTotalCents,
            otherDiscountAmount,
          });
        }
      }
      stopTrace(TraceName.PRICE_ORDER, { rbiOrderId: rbiOrderId ?? '' });
      if (diagnosticsEnabledRef.current) {
        safelyObserveDiagnostics(() => {
          diagnosticPricingRef.current.recordPollResult(
            diagnosticAttemptByOrderIdRef.current.get(rbiOrder.rbiOrderId) ?? null,
            {
              phase: 'succeeded',
              rbiOrderId: rbiOrder.rbiOrderId,
              pricingStatus: String(rbiOrder.status ?? ''),
              orderStatus: String(rbiOrder.status ?? ''),
              deliveryStatus: rbiOrder.delivery?.status
                ? String(rbiOrder.delivery.status)
                : undefined,
              returnedTotal: diagnosticMoney({
                minorAmount: rbiOrder.cart.totalCents,
                formatted: formatCurrencyForLocaleRef.current(rbiOrder.cart.totalCents),
                source: 'pricing_backend',
                availability: 'available',
              }),
            }
          );
        });
      }
    },
    orderStatusPollInterval,
    rbiOrderId,
    successStatuses: {
      delivery: DeliveryStatus.QUOTE_SUCCESSFUL,
      pos: successStatuses,
    },
    skip: !enabled || (!rbiOrderId && isCartTotalNegative && mutationResult.loading),
  });

  const {
    orderStatus,
    serverOrder,
    failure: priceOrderFailure,
    success: priceOrderSuccess,
    isPricingFailureRecoverable,
    isLoading,
  } = orderStatusQueryResult;

  const isDeliveryOrderLoading =
    isDelivery(serverOrder?.cart.serviceMode) && !serverOrder?.delivery;

  // priceOrderFailure && isPricingFailureRecoverable toggle during repricing,
  // so we check orderPricingAttemptsRef > 0 to maintain a stable true value
  // throughout the retry process, preventing UI flicker in dependent components.
  const isOrderPricingRetryInProgress =
    Boolean(serverOrder?.status && priceOrderErrorStatuses.includes(serverOrder.status)) &&
    ((priceOrderFailure && isPricingFailureRecoverable) || orderPricingAttemptsRef.current > 0) &&
    !didOrderPricingRetryFail;

  // serverOrder?.rbiOrderId !== rbiOrderId check covers the brief moment during
  // which the mutation finished but the order status query hasn't yet started.
  // Without this, `loading` is set to `false` momentarily, which causes the UI
  // to become enabled for that brief period only to then become disabled when
  // the order status query begins.
  const loading =
    mutationResult.loading ||
    (serverOrder?.rbiOrderId || null) !== (rbiOrderId || null) ||
    isLoading ||
    isDeliveryOrderLoading;

  const acceptMutationResult = useCallback(
    (
      sequence: number,
      attemptId: number | null,
      requestContext: PricingCartRequestContext | null,
      result: { data?: { priceOrder?: { rbiOrderId?: string } } | null }
    ) => {
      if (pricingSequenceRef.current !== sequence) {
        logger.warn({
          message: '[usePriceOrder] Discarding stale priceOrder response',
          context: {
            expectedSequence: pricingSequenceRef.current,
            receivedSequence: sequence,
          },
        });
        return;
      }
      const orderId = result.data?.priceOrder?.rbiOrderId ?? null;
      if (orderId) {
        acceptedPricingCartRequestRef.current =
          requestContext?.sequence === sequence ? { ...requestContext, rbiOrderId: orderId } : null;
        setAcceptedRbiOrderId(orderId);
        if (diagnosticsEnabledRef.current) {
          safelyObserveDiagnostics(() => {
            diagnosticPricingRef.current.recordMutationResult(attemptId, { rbiOrderId: orderId });
            diagnosticAttemptByOrderIdRef.current.set(orderId, attemptId);
            if (diagnosticAttemptByOrderIdRef.current.size > 8) {
              const oldestOrderId = diagnosticAttemptByOrderIdRef.current.keys().next().value;
              if (oldestOrderId) {
                diagnosticAttemptByOrderIdRef.current.delete(oldestOrderId);
              }
            }
          });
        }
      } else if (diagnosticsEnabledRef.current) {
        safelyObserveDiagnostics(() => {
          diagnosticPricingRef.current.recordMutationResult(attemptId, {
            error: { code: 'MISSING_RBI_ORDER_ID', message: 'Pricing returned no RBI order ID.' },
          });
        });
      }
    },
    []
  );

  // Note that this actually just requests a price order. the result of this process is a polling we
  // do in the `useOrderStatus` hook.
  const priceOrder = useCallback(
    (options: Omit<IPriceOrderParams, 'cartEntries' | 'redeemReward'>) => {
      if (!enabled) {
        return;
      }

      const isCartInvalid = !cartEntries.length || isCartTotalNegative;

      if (isCartInvalid || isSignUpAfterCart) {
        return;
      }

      const input = buildPriceOrderInput({ cartEntries, vendor, options });

      if (!input) {
        return;
      }

      const delivery = buildPriceDeliveryInput(options, quoteId);
      const mutationVariables = { input, delivery };

      syncStorage.setObject(SyncStorageKey.PRICE_ORDER_INPUT, mutationVariables);

      // Dispatch action to indicate pricing has started
      dispatch(actions.ordering.setIsPricingOrder(true));
      pricingRepriceRequestIdRef.current = checkoutRepriceRequestId;

      const currentSequence = ++pricingSequenceRef.current;
      const pricingCartRequest = {
        sequence: currentSequence,
        cartIdentity: currentCartIdentity,
        requestedAmountCents: options.requestedAmountCents,
      };
      latestPricingCartRequestRef.current = pricingCartRequest;
      acceptedPricingCartRequestRef.current = null;
      let diagnosticAttemptId: number | null = null;
      if (diagnosticsEnabledRef.current) {
        safelyObserveDiagnostics(() => {
          const requestedTotal = diagnosticInputRef.current.cartTotal;
          diagnosticAttemptId = diagnosticPricingRef.current.beginAttempt(
            'priceOrder',
            projectPricingInput({
              storeNumber: diagnosticInputRef.current.storeNumber ?? undefined,
              serviceMode: diagnosticInputRef.current.serviceMode ?? undefined,
              cartEntryCount: cartEntries.length,
              requestedTotal: diagnosticMoney({
                minorAmount: requestedTotal,
                formatted:
                  requestedTotal === undefined
                    ? undefined
                    : formatCurrencyForLocaleRef.current(requestedTotal),
                source: 'pricing_request',
                availability: 'available',
              }),
              checkoutRepriceRequestId: String(checkoutRepriceRequestId),
            })
          );
        });
      }
      diagnosticAttemptRef.current = diagnosticAttemptId;

      startTrace(TraceName.PRICE_ORDER);

      return fireMutation({
        variables: mutationVariables,
      }).then(
        result => {
          acceptMutationResult(currentSequence, diagnosticAttemptId, pricingCartRequest, result);
          return result;
        },
        error => {
          if (diagnosticsEnabledRef.current) {
            safelyObserveDiagnostics(() => {
              diagnosticPricingRef.current.recordFailure(
                diagnosticAttemptId,
                projectDiagnosticError(error)
              );
            });
          }
          throw error;
        }
      );
    },
    [
      cartEntries,
      currentCartIdentity,
      checkoutRepriceRequestId,
      enabled,
      isSignUpAfterCart,
      isCartTotalNegative,
      vendor,
      quoteId,
      fireMutation,
      dispatch,
      acceptMutationResult,
    ]
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (priceOrderFailure) {
      if (!serverOrder || !priceOrderErrorStatuses.includes(serverOrder.status)) {
        return;
      }

      const isMaxRetryLimitReached = orderPricingAttemptsRef.current === PRICING_RETRY_LIMIT;

      if (isMaxRetryLimitReached) {
        setDidOrderPricingRetryFail(true);
        if (diagnosticsEnabledRef.current) {
          safelyObserveDiagnostics(() => {
            diagnosticPricingRef.current.recordPollResult(
              diagnosticAttemptByOrderIdRef.current.get(serverOrder.rbiOrderId) ?? null,
              {
                phase: 'failed',
                rbiOrderId: serverOrder.rbiOrderId,
                pricingStatus: String(serverOrder.status),
                orderStatus: String(serverOrder.status),
                deliveryStatus: serverOrder.delivery?.status
                  ? String(serverOrder.delivery.status)
                  : undefined,
                error: {
                  code: String(serverOrder.status),
                  message: 'Pricing retry limit reached.',
                },
              }
            );
          });
        }
        return;
      }

      const mutationVariables = syncStorage.getObject<{
        input: IPriceOrderInput;
        delivery: IPriceDeliveryInput;
      }>(SyncStorageKey.PRICE_ORDER_INPUT);

      if (mutationVariables === null) {
        logger.error('Price order mutation variables are null, cannot retry pricing.');
        return;
      }

      orderPricingAttemptsRef.current += 1;
      if (diagnosticsEnabledRef.current) {
        safelyObserveDiagnostics(() => {
          diagnosticPricingRef.current.recordRequest(diagnosticAttemptRef.current, {
            checkoutRepriceRequestId: String(pricingRepriceRequestIdRef.current),
          });
        });
      }

      const retrySequence = pricingSequenceRef.current;

      startTrace(TraceName.PRICE_ORDER);

      fireMutation({
        variables: mutationVariables,
      }).then(
        result => {
          acceptMutationResult(
            retrySequence,
            diagnosticAttemptRef.current,
            latestPricingCartRequestRef.current,
            result
          );
        },
        error => {
          if (diagnosticsEnabledRef.current) {
            safelyObserveDiagnostics(() => {
              diagnosticPricingRef.current.recordFailure(
                diagnosticAttemptRef.current,
                projectDiagnosticError(error)
              );
            });
          }
          throw error;
        }
      );
    }
  }, [
    priceOrderFailure,
    enabled,
    isPricingFailureRecoverable,
    isOrderPricingRetryInProgress,
    fireMutation,
    serverOrder,
    acceptMutationResult,
  ]);

  const setIsRepricing = useCallback(() => {
    setDidOrderPricingRetryFail(false);
    orderPricingAttemptsRef.current = 0;
  }, [setDidOrderPricingRetryFail]);

  // Set isPricingOrder to false when pricing completes (success or failure)
  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (priceOrderSuccess || priceOrderFailure) {
      dispatch(actions.ordering.setIsPricingOrder(false));
    }
  }, [enabled, priceOrderSuccess, priceOrderFailure, dispatch]);

  const acceptedPricingCartRequest = acceptedPricingCartRequestRef.current;
  const isServerOrderForCurrentCart = Boolean(
    serverOrder &&
      acceptedPricingCartRequest &&
      serverOrder.rbiOrderId === acceptedPricingCartRequest.rbiOrderId &&
      currentCartIdentity === acceptedPricingCartRequest.cartIdentity &&
      Math.max(0, cartTotal) === acceptedPricingCartRequest.requestedAmountCents
  );

  return {
    loading,
    orderStatus,
    priceOrderFailure,
    priceOrderSuccess,
    priceResult: mutationResult,
    rbiOrderId,
    serverOrder,
    priceOrder,
    isOrderPricingRetryInProgress,
    isPricingFailureRecoverable,
    didOrderPricingRetryFail,
    setIsRepricing,
    isServerOrderForCurrentCart,
  } as const;
};
